import { Box, Collapse, LinearProgress, Stack, Typography } from '@mui/material';
import { Button, IconButton } from 'design-system';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { MdAdd, MdClose, MdDelete, MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { showField, showFieldNew } from 'utils/form';
import {
  Field,
  FieldGroupFragment as FieldGroup,
  FieldGroupOrientation,
  FieldGroupVariant,
  FieldVariant,
  FormType,
  InputType,
} from 'generated/graphql';
import DynamicInputField from './DynamicInputField';
import useRemoveFieldGroup from './Form/useRemoveFieldGroup';

interface DynamicFieldGroupProps {
  groupIndex: number;
  fieldGroup: FieldGroup;
  fieldGroups: FieldGroup[];
  isLoading?: boolean;
  numOfGroups: number;
  onFieldUpdateNew: (
    fieldGroup: Field[],
    field: Field,
    value: string,
    values?: string[],
    entityID?: string,
  ) => Promise<void>;
  setNumOfGroups: (numOfGroups: number) => void;
  formType: FormType;
  formFields: Field[];
  decorateField?: (field: Field, fieldGroup: FieldGroup) => Field | null;
  renderRowActions?: (fieldGroup: FieldGroup) => ReactNode;
  showDivider?: boolean;
  fixedRows?: boolean;
}

const FLEX_NONE_FIELD_TYPES = [InputType.Images, InputType.Files, InputType.ButtonUrl, InputType.Numberstepper];
const FLEX_GROW_FIELD_TYPES = [InputType.Autocomplete, InputType.Text];

/** Splits visible (non-hidden) fields into rows for layout.
 *
 * Standard fields pack 2 or 3 per row (3 only when the count is divisible by 3).
 *
 * @param fields - The fields to split into rows
 * @returns An array of rows which each contain an array of fields
 */
const getFieldGroupRows = (fields: Field[]): Field[][] => {
  const visibleFields = fields.filter((field) => field.type !== InputType.Hidden);
  if (visibleFields.length === 0) return [];

  const blockFields = visibleFields.filter((f) => f.variant === FieldVariant.Detailed);
  const inlineFields = visibleFields.filter((f) => f.variant !== FieldVariant.Detailed);
  const n = inlineFields.length;

  const rows: Field[][] = [];
  if (n > 0) {
    if (n % 3 === 0) {
      for (let i = 0; i < n; i += 3) {
        rows.push(inlineFields.slice(i, i + 3));
      }
    } else {
      const fullRows = Math.floor(n / 2);
      const remainder = n % 2;
      for (let i = 0; i < fullRows; i++) {
        rows.push(inlineFields.slice(i * 2, i * 2 + 2));
      }
      if (remainder > 0) {
        rows.push(inlineFields.slice(n - remainder, n));
      }
    }
  }
  for (const f of blockFields) {
    rows.push([f]);
  }
  return rows;
};

// Check if a field group has any filled or partially filled fields
const hasFilledFields = (fieldGroup: FieldGroup): boolean => {
  return fieldGroup.fields.some((field) => {
    // Skip hidden fields
    if (field.type === InputType.Hidden) {
      return false;
    }
    // Check if field has a non-empty value
    if (field.value && field.value.trim() !== '') {
      return true;
    }
    // Check if field has non-empty values array
    if (field.values && field.values.length > 0 && field.values.some((v) => v && v.trim() !== '')) {
      return true;
    }
    return false;
  });
};

const DynamicFieldGroup = ({
  groupIndex,
  fieldGroup,
  fieldGroups,
  isLoading,
  numOfGroups,
  onFieldUpdateNew,
  setNumOfGroups,
  formType,
  formFields,
  decorateField,
  renderRowActions,
  showDivider,
  fixedRows,
}: DynamicFieldGroupProps) => {
  const { onDeleteGroup } = useRemoveFieldGroup({
    fieldGroups,
    groupIndex,
    onFieldUpdateNew,
    numOfGroups,
    setNumOfGroups,
  });
  // Initialize expanded state: true if group has filled fields, false otherwise
  const [expanded, setExpanded] = useState(() => hasFilledFields(fieldGroup));
  const isExpandVariant = fieldGroup.variant === FieldGroupVariant.Expand;

  const fieldsForRender = decorateField
    ? fieldGroup.fields.map((f) => decorateField(f, fieldGroup)).filter((f): f is Field => f !== null)
    : fieldGroup.fields;

  const rows = getFieldGroupRows(fieldsForRender);

  const fieldsContent = (
    <Box
      sx={(theme) => ({
        ...(fieldGroup.orientation === FieldGroupOrientation.Horizontal && {
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
        }),
        ...(showDivider && {
          borderTop: `1px solid ${theme.palette.divider}`,
          paddingTop: theme.spacing(1.5),
          marginTop: theme.spacing(1.5),
        }),
      })}
    >
      {rows.map((fieldsRow, rowIndex) => (
        <Box
          key={`group-row-${rowIndex}`}
          sx={{ display: 'flex', flexDirection: 'row', gap: 1, width: '100%', alignItems: 'center' }}
        >
          {fieldsRow.map((field, fieldIndexInRow) => {
            if (!showField(field, formFields) || !showFieldNew(field, formFields)) {
              return null;
            }
            return (
              <Box
                key={`${field.id}-${field.fieldGroupID}`}
                sx={{
                  flex: FLEX_NONE_FIELD_TYPES.includes(field.type)
                    ? 'none'
                    : FLEX_GROW_FIELD_TYPES.includes(field.type)
                      ? 3
                      : 1,
                }}
              >
                <DynamicInputField
                  field={field}
                  fieldGroup={fieldGroup.fields}
                  disabled={isLoading}
                  autoFocus={rowIndex === 0 && fieldIndexInRow === 0}
                  allFields={formFields}
                  onChange={(value, values, entityID) =>
                    onFieldUpdateNew(fieldGroup.fields, field, value, values, entityID)
                  }
                  sx={{ width: '100%' }}
                  formType={formType}
                />
              </Box>
            );
          })}

          {rowIndex === 0 && !isExpandVariant && (
            <>
              {renderRowActions?.(fieldGroup)}
              {!fixedRows && (
                <IconButton
                  key="delete-button"
                  disabled={isLoading}
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={onDeleteGroup}
                  sx={(theme) => ({
                    flexShrink: 0,
                    flexGrow: 0,
                    width: theme.spacing(4),
                    height: theme.spacing(4),
                    minWidth: theme.spacing(4),
                  })}
                >
                  <MdClose size={20} />
                </IconButton>
              )}
            </>
          )}
        </Box>
      ))}
    </Box>
  );

  if (isExpandVariant) {
    return (
      <Box
        sx={(theme) => ({
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: 1,
          padding: theme.spacing(2),
          mb: theme.spacing(2),
        })}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: expanded ? 1 : 0,
          }}
        >
          <Stack direction="row">
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              disabled={isLoading}
              aria-label={expanded ? 'collapse' : 'expand'}
            >
              {expanded ? <MdKeyboardArrowUp size={20} /> : <MdKeyboardArrowDown size={20} />}
            </IconButton>
            <Typography
              sx={(theme) => ({
                fontSize: '1rem',
                fontWeight: theme.typography.fontWeightMedium,
              })}
            >
              {fieldGroup.title ?? ''}
            </Typography>
          </Stack>
          <IconButton disabled={isLoading} size="small" aria-label="delete" color="error" onClick={onDeleteGroup}>
            <MdDelete size={20} />
          </IconButton>
        </Box>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {fieldsContent}
        </Collapse>
      </Box>
    );
  }

  return fieldsContent;
};

const DynamicFieldGroups = ({
  fieldGroups,
  isLoading,
  onFieldUpdateNew,
  formType,
  formFields,
  decorateField,
  renderRowActions,
  cardDividers = false,
  fixedRows = false,
}: {
  fieldGroups: FieldGroup[];
  isLoading?: boolean;
  onFieldUpdateNew: (
    fieldGroup: Field[],
    field: Field,
    value: string,
    values?: string[],
    entityID?: string,
  ) => Promise<void>;
  formType: FormType;
  formFields: Field[];
  decorateField?: (field: Field, fieldGroup: FieldGroup) => Field | null;
  renderRowActions?: (fieldGroup: FieldGroup) => ReactNode;
  cardDividers?: boolean;
  fixedRows?: boolean;
}) => {
  // Initialize numOfGroups to show all groups that have filled/partially filled fields
  const [numOfGroups, setNumOfGroups] = useState(() => {
    if (fixedRows) {
      return fieldGroups.length;
    }

    // Find the last index of a group with filled fields
    let lastFilledIndex = -1;
    fieldGroups.forEach((group, index) => {
      if (hasFilledFields(group)) {
        lastFilledIndex = index;
      }
    });
    // Show at least 1 group, or all groups up to and including the last filled group
    // But don't exceed the total number of groups
    return Math.min(fieldGroups.length, Math.max(1, lastFilledIndex + 1));
  });

  const isFirstRender = useRef(true);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [numOfGroups]);

  return (
    <Box
      sx={(theme) => ({
        marginTop: theme.spacing(1),
        [theme.breakpoints.down('md')]: {
          maxHeight: 320,
          overflowY: 'auto',
        },
      })}
    >
      {fieldGroups
        .map((fieldGroup, index) => {
          return (
            <DynamicFieldGroup
              key={`${fieldGroup.fields[0].fieldGroupID}-${fieldGroup.fields[0].value}`}
              groupIndex={index}
              fieldGroup={fieldGroup}
              fieldGroups={fieldGroups}
              isLoading={isLoading}
              numOfGroups={numOfGroups}
              setNumOfGroups={setNumOfGroups}
              onFieldUpdateNew={onFieldUpdateNew}
              formType={formType}
              formFields={formFields}
              decorateField={decorateField}
              renderRowActions={renderRowActions}
              showDivider={cardDividers && index > 0}
              fixedRows={fixedRows}
            />
          );
        })
        .slice(0, numOfGroups)}
      <Box sx={{ height: 4, mt: 1 }} aria-live="polite">
        {isLoading && <LinearProgress aria-label="Saving product" />}
      </Box>
      {!fixedRows && (
        <Button
          fullWidth
          sx={(theme) => ({ margin: theme.spacing(1, 0, 1) })}
          variant="outlined"
          color="primary"
          startIcon={<MdAdd size={14} />}
          disabled={isLoading || numOfGroups === fieldGroups.length}
          onClick={() => {
            if (numOfGroups < fieldGroups.length) setNumOfGroups(numOfGroups + 1);
          }}
        >
          {numOfGroups < fieldGroups.length ? 'Add' : 'Max'}
        </Button>
      )}
      <div ref={bottomRef} />
    </Box>
  );
};
export default DynamicFieldGroups;
